import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Panel, PanelType, Icon, PrimaryButton, Toggle } from "@fluentui/react";
import { WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";
import { Formik } from "formik";
import * as Yup from "yup";

import { createLookupListValue } from "../../../../store/lookupLists/actions";
import { LookupListsResponse } from "../../../../models/responseModels/lookupLists/LookupListsResponse";
import { IApplicationState } from "../../../../models/interfaces/IApplicationState";
import { ErrorTypes } from "../../../../models/enums/ErrorTypes";
import { AlertComponent } from "../../../reusable/alertComponent/AlertComponent";

export interface NewLookupListValue {
	name: string;
	isActive: boolean;
}

export interface CreateListValuePanelProps {
	lookupList: LookupListsResponse | null;
	isOpen: boolean;
	onClose: () => void;
}

const CreateListValuePanel = (props: WrappedComponentProps & CreateListValuePanelProps) => {
	const { lookupList, isOpen, onClose, intl } = props;
	const dispatch = useDispatch();
	const error = useSelector((state: IApplicationState) => state.lookupListsSlice.error);

	if (lookupList === null || !isOpen) {
		return null;
	}

	const initialValues: NewLookupListValue = {
		name: "",
		isActive: true,
	};
	return (
		<Panel
			className="one-panel"
			isOpen={isOpen}
			type={PanelType.smallFixedFar}
			onDismiss={onClose}
			isLightDismiss
			onLightDismissClick={onClose}
			closeButtonAriaLabel="Close"
		>
			<section className="one-panel__panel-header">
				<div className="one-panel__panel-header--flex">
					<h1>
						<FormattedMessage id="general.new" defaultMessage="Nieuw" />{" "}
						{lookupList.name}
					</h1>

					<div className="one-panel__panel-close" onClick={onClose}>
						<Icon iconName="ChromeClose" />
					</div>
				</div>
			</section>
			<section className="one-panel__panel-content">
				{error.type === ErrorTypes.OnCreate && (
					<AlertComponent type="danger" message={error.message} />
				)}

				<div className="form-wrapper">
					<Formik
						enableReinitialize
						initialValues={initialValues}
						onSubmit={(values) => {
							dispatch(createLookupListValue(lookupList, values));
						}}
						validationSchema={Yup.object().shape({
							name: Yup.string()
								.min(
									2,
									intl.formatMessage({
										id:
											"lookup.panel.create.create.validation.name.minTwoChars",
										defaultMessage:
											"De waarde moet ten minste twee tekens lang zijn.",
									})
								)
								.required(
									intl.formatMessage({
										id: "general.validation.required",
										defaultMessage: "Dit veld is verplicht.",
									})
								),
						})}
					>
						{(props) => {
							const {
								values,
								touched,
								errors,
								isSubmitting,
								handleChange,
								handleBlur,
								handleSubmit,
								setFieldValue,
							} = props;
							return (
								<form onSubmit={handleSubmit}>
									<div className="form-field-label">
										<label>
											<FormattedMessage
												id="lookup.panel.create.label.name"
												defaultMessage="Waarde"
											/>
										</label>
										<span className="form-field-label__required-star">*</span>
									</div>

									{errors.name && touched.name && (
										<div className="input-feedback">{errors.name}</div>
									)}

									<input
										id="name"
										placeholder={intl.formatMessage({
											id: "lookup.panel.create.placeholder.name",
											defaultMessage: "Vul een waarde in",
										})}
										type="text"
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.name}
										className={errors.name && touched.name ? "error" : ""}
									/>

									<div className="form-field-label">
										<label>
											<FormattedMessage
												id="lookup.panel.create.label.isActive"
												defaultMessage="In gebruik"
											/>
										</label>
										<span className="form-field-label__required-star">*</span>
									</div>

									{errors.isActive && touched.isActive && (
										<div className="input-feedback">{errors.isActive}</div>
									)}
									<Toggle
										checked={values.isActive}
										className="toggle-input"
										onText={intl.formatMessage({
											id: "general.yes",
											defaultMessage: "Ja",
										})}
										offText={intl.formatMessage({
											id: "general.no",
											defaultMessage: "Nee",
										})}
										onChange={() => setFieldValue("isActive", !values.isActive)}
									/>

									<PrimaryButton
										text={intl.formatMessage({
											id: "general.create",
											defaultMessage: "Aanmaken",
										})}
										type="submit"
										disabled={
											isSubmitting ||
											Object.keys(touched).length === 0 ||
											(Object.keys(errors).length !== 0 &&
												errors.constructor === Object)
										}
									/>
								</form>
							);
						}}
					</Formik>
				</div>
			</section>
		</Panel>
	);
};

export const CreateListValuePanelComponent = injectIntl(CreateListValuePanel);
